import { useEffect, useState } from "react";
import { Gift, Copy, Check, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { errorMessage } from "@/lib/errors";

/**
 * Admin Referrals tab — read & write the global referral program config,
 * see all referral_events, mark events as redeemed.
 *
 * Embedded inside AdminDashboard.js as <AdminReferralsTab /> when the
 * activeTab === "referrals".
 */
export default function AdminReferralsTab() {
  const [program, setProgram] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [copiedId, setCopiedId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [progRes, listRes] = await Promise.all([
        api.get("/admin/referral/program"),
        api.get("/admin/referral/list"),
      ]);
      setProgram(progRes.data);
      setEvents(listRes.data);
    } catch {
      toast.error("Failed to load referral program");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateProgram = async (patch) => {
    setSaving(true);
    try {
      const res = await api.patch("/admin/referral/program", patch);
      setProgram(res.data);
      toast.success("Referral program updated");
    } catch (err) {
      toast.error(errorMessage(err, "Update failed"));
    } finally {
      setSaving(false);
    }
  };

  const redeem = async (eventId) => {
    const bookingId = prompt("Optional: enter the booking ID this referral is being redeemed against. Leave blank to redeem without a booking link.") || "";
    try {
      await api.post(`/admin/referral/${eventId}/redeem`, { booking_id: bookingId });
      toast.success("Marked as redeemed");
      load();
    } catch (err) {
      toast.error(errorMessage(err, "Redeem failed"));
    }
  };

  const copyCode = async (code, id) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch {
      toast.error("Copy failed");
    }
  };

  if (loading) return <div className="text-white/40 font-body text-sm">Loading referral program...</div>;
  if (!program) return null;

  const filtered = statusFilter === "all" ? events : events.filter(e => e.status === statusFilter);
  const totalReferrals = events.length;
  const redeemed = events.filter(e => e.status === "redeemed").length;
  const pending = totalReferrals - redeemed;

  return (
    <div className="space-y-8" data-testid="admin-referrals-tab">
      {/* Program controls */}
      <div className="bg-white/[0.02] border border-white/5 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Gift size={16} className="text-gold" />
          <h3 className="text-white font-heading text-lg">Referral Program</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <label className="block">
            <span className="text-white/40 text-[10px] font-body uppercase tracking-wider">Discount Percentage</span>
            <div className="flex items-center gap-2 mt-1">
              <input
                type="number"
                min="0" max="100" step="0.5"
                defaultValue={program.current_percentage}
                onBlur={e => {
                  const v = parseFloat(e.target.value);
                  if (!isNaN(v) && v !== program.current_percentage) updateProgram({ current_percentage: v });
                }}
                className="w-24 bg-black/40 border border-white/10 text-white text-sm px-3 py-2 font-body focus:outline-none focus:border-gold/40"
                data-testid="referral-percentage-input"
              />
              <span className="text-gold font-heading text-2xl">%</span>
            </div>
          </label>
          <label className="block">
            <span className="text-white/40 text-[10px] font-body uppercase tracking-wider">Program Name</span>
            <input
              type="text"
              defaultValue={program.name}
              onBlur={e => { if (e.target.value !== program.name) updateProgram({ name: e.target.value }); }}
              className="w-full bg-black/40 border border-white/10 text-white text-sm px-3 py-2 font-body focus:outline-none focus:border-gold/40 mt-1"
              maxLength={100}
              data-testid="referral-name-input"
            />
          </label>
          <label className="block">
            <span className="text-white/40 text-[10px] font-body uppercase tracking-wider">Active</span>
            <button
              onClick={() => updateProgram({ active: !program.active })}
              disabled={saving}
              className={`block w-full px-4 py-2 mt-1 font-body text-sm transition-colors ${program.active ? "bg-gold/15 text-gold border border-gold/30" : "bg-white/5 text-white/40 border border-white/10"} disabled:opacity-40`}
              data-testid="referral-active-toggle"
            >
              {program.active ? "Active" : "Paused"}
            </button>
          </label>
        </div>
        <label className="block">
          <span className="text-white/40 text-[10px] font-body uppercase tracking-wider">Public description</span>
          <textarea
            defaultValue={program.description}
            onBlur={e => { if (e.target.value !== program.description) updateProgram({ description: e.target.value }); }}
            className="w-full bg-black/40 border border-white/10 text-white text-sm px-3 py-2 font-body focus:outline-none focus:border-gold/40 mt-1"
            rows={2}
            maxLength={500}
            data-testid="referral-description-input"
          />
        </label>
        <p className="text-white/30 text-[11px] font-body mt-3">
          Tip: changes apply to NEW referrals only — existing pending referrals keep the percentage they were issued at.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-px bg-white/5">
        <div className="bg-luxury-charcoal p-4 text-center">
          <p className="text-[10px] font-body uppercase tracking-wider text-white/30">Total Referrals</p>
          <p className="text-white font-heading text-2xl mt-1" data-testid="ref-stat-total">{totalReferrals}</p>
        </div>
        <div className="bg-luxury-charcoal p-4 text-center">
          <p className="text-[10px] font-body uppercase tracking-wider text-white/30">Pending</p>
          <p className="text-amber-400 font-heading text-2xl mt-1" data-testid="ref-stat-pending">{pending}</p>
        </div>
        <div className="bg-luxury-charcoal p-4 text-center">
          <p className="text-[10px] font-body uppercase tracking-wider text-white/30">Redeemed</p>
          <p className="text-gold font-heading text-2xl mt-1" data-testid="ref-stat-redeemed">{redeemed}</p>
        </div>
      </div>

      {/* Filter + list */}
      <div className="bg-white/[0.02] border border-white/5">
        <div className="flex items-center justify-between p-4 border-b border-white/5">
          <h4 className="text-white font-heading text-sm">Referral Events</h4>
          <div className="flex gap-1">
            {["all", "signed_up", "redeemed"].map(s => (
              <button key={s} onClick={() => setStatusFilter(s)} className={`text-[10px] font-body px-3 py-1 transition-colors ${statusFilter === s ? "bg-gold/15 text-gold" : "text-white/40 hover:text-white"}`}>
                {s.replace("_", " ")}
              </button>
            ))}
          </div>
        </div>
        {filtered.length === 0 ? (
          <p className="text-white/30 text-sm font-body p-6 text-center">No referrals yet.</p>
        ) : (
          <div className="divide-y divide-white/5">
            {filtered.map(e => (
              <div key={e.id} className="p-4 flex items-center gap-4" data-testid={`ref-event-${e.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-body text-sm truncate">
                    <span className="text-gold">{e.referrer_email}</span>
                    <span className="text-white/30 mx-2">→</span>
                    <span>{e.referred_email}</span>
                  </p>
                  <p className="text-white/40 text-[10px] font-body mt-0.5 flex items-center gap-2">
                    <span>{new Date(e.signup_date).toLocaleDateString()}</span>
                    <span>•</span>
                    <span className="text-gold">{e.percentage_at_signup}% locked</span>
                    <span>•</span>
                    <button onClick={() => copyCode(e.referrer_code, e.id)} className="flex items-center gap-1 text-white/40 hover:text-gold transition-colors">
                      {copiedId === e.id ? <Check size={9} className="text-green-400" /> : <Copy size={9} />}
                      {e.referrer_code}
                    </button>
                  </p>
                </div>
                {e.status === "redeemed" ? (
                  <span className="text-gold text-[10px] font-body uppercase tracking-wider">Redeemed</span>
                ) : (
                  <button onClick={() => redeem(e.id)} className="text-[10px] font-body px-3 py-1.5 bg-gold/15 text-gold hover:bg-gold/25 transition-colors" data-testid={`redeem-${e.id}`}>
                    Mark Redeemed
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="text-white/30 text-[11px] font-body text-center">
        Need to share a code with a client?{" "}
        <a href="/" target="_blank" className="text-gold hover:underline inline-flex items-center gap-1">
          Public signup page <ExternalLink size={9} />
        </a>
      </p>
    </div>
  );
}
